import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { useEffect, useState } from 'react';
import { useUpdateUser } from '../../hooks/useUsers';
import type { UpdateUserBody, UserDetail } from '../../types/user';

interface EditUserDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  user?: Partial<UserDetail>;
}

interface FormState {
  firstName: string;
  secondName: string;
  preferredName: string;
  personalEmail: string;
  personalPhone: string;
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
}

const EMPTY: FormState = {
  firstName: '',
  secondName: '',
  preferredName: '',
  personalEmail: '',
  personalPhone: '',
  dateOfBirth: '',
  gender: '',
  maritalStatus: '',
};

// The backend returns ISO datetimes for date fields; the <input type="date"> needs YYYY-MM-DD.
const toDateInput = (value: string | null | undefined): string =>
  value && value.length >= 10 ? value.slice(0, 10) : '';

export function EditUserDetailsDialog({
  open,
  onOpenChange,
  userId,
  user,
}: EditUserDetailsDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const updateUser = useUpdateUser();

  useEffect(() => {
    if (open) {
      setForm({
        firstName: user?.firstName ?? '',
        secondName: user?.secondName ?? '',
        preferredName: user?.preferredName ?? '',
        personalEmail: user?.personalEmail ?? '',
        personalPhone: user?.personalPhone ?? '',
        dateOfBirth: toDateInput(user?.dateOfBirth),
        gender: user?.gender ?? '',
        maritalStatus: user?.maritalStatus ?? '',
      });
    }
  }, [open, user]);

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    // Only send keys the user actually filled in so we don't overwrite nulls.
    const data: UpdateUserBody = {};
    for (const [key, value] of Object.entries(form)) {
      if (value !== '') {
        (data as Record<string, unknown>)[key] = value;
      }
    }
    await updateUser.mutateAsync({ userId, data });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit User Details</DialogTitle>
          <DialogDescription>Update basic personal and contact information.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <Input
              id="firstName"
              value={form.firstName}
              onChange={(e) => handleChange('firstName', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="secondName">Last Name</Label>
            <Input
              id="secondName"
              value={form.secondName}
              onChange={(e) => handleChange('secondName', e.target.value)}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="preferredName">Preferred Name</Label>
            <Input
              id="preferredName"
              value={form.preferredName}
              onChange={(e) => handleChange('preferredName', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="personalEmail">Personal Email</Label>
            <Input
              id="personalEmail"
              type="email"
              value={form.personalEmail}
              onChange={(e) => handleChange('personalEmail', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="personalPhone">Personal Phone</Label>
            <Input
              id="personalPhone"
              value={form.personalPhone}
              onChange={(e) => handleChange('personalPhone', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="dateOfBirth">Date of Birth</Label>
            <Input
              id="dateOfBirth"
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => handleChange('dateOfBirth', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="gender">Gender</Label>
            <Select value={form.gender} onValueChange={(v) => handleChange('gender', v)}>
              <SelectTrigger id="gender">
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
                <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="maritalStatus">Marital Status</Label>
            <Select
              value={form.maritalStatus}
              onValueChange={(v) => handleChange('maritalStatus', v)}
            >
              <SelectTrigger id="maritalStatus">
                <SelectValue placeholder="Select marital status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Single">Single</SelectItem>
                <SelectItem value="Married">Married</SelectItem>
                <SelectItem value="Divorced">Divorced</SelectItem>
                <SelectItem value="Widowed">Widowed</SelectItem>
                <SelectItem value="Civil Partnership">Civil Partnership</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateUser.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateUser.isPending}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {updateUser.isPending ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
